import { Box, Drawer, Flex, Text, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconChevronLeft } from '@tabler/icons-react';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useContainerQuery } from 'src/UI/app-container/ContainerBreakpoint';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { SidebarMenuContext } from './MenuTriggerSlot';
import { Sidebar } from './sidebar/Sidebar';
import { SidebarItem } from './sidebar/types';
import { FONT_WEIGHTS } from '../theme/theme';

type PageWithSidebarProps = {
  links: SidebarItem[];
  activeUrl: string;
  children: ReactNode;
};

export default function PageWithSidebar({ links, activeUrl, children }: PageWithSidebarProps) {
  const cq = useContainerQuery();
  const { flagEnabledResponsiveMenus } = useFlags();
  const isCollapsed = flagEnabledResponsiveMenus && cq({ base: true, sm: false });
  const [opened, { open, close }] = useDisclosure(false);
  const [firstRender, setFirstRender] = useState(true);
  const [customTriggerCount, setCustomTriggerCount] = useState(0);
  const hasCustomTrigger = customTriggerCount > 0;

  const registerCustomTrigger = useCallback(() => {
    setCustomTriggerCount(c => c + 1);
    return () => setCustomTriggerCount(c => c - 1);
  }, []);

  const sidebarMenuValue = useMemo(
    () => ({ open, isCollapsed, registerCustomTrigger }),
    [open, isCollapsed, registerCustomTrigger],
  );

  useEffect(() => {
    setFirstRender(false);
  }, []);

  useEffect(() => {
    if (!isCollapsed && opened) close();
  }, [isCollapsed, opened, close]);

  const inlineFallback = (
    <UnstyledButton
      onClick={open}
      aria-haspopup="dialog"
      aria-expanded={opened}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
    >
      <IconChevronLeft size={16} color="var(--mantine-color-gray-8)" />
      <Text
        size="md"
        style={{
          color: 'var(--mantine-color-gray-8)',
          fontWeight: FONT_WEIGHTS.medium,
        }}
      >
        Menu
      </Text>
    </UnstyledButton>
  );

  return (
    <SidebarMenuContext.Provider value={sidebarMenuValue}>
      {isCollapsed ? (
        <>
          {renderInlineFallback(inlineFallback, hasCustomTrigger, firstRender)}
          <Box>{children}</Box>
        </>
      ) : (
        <Flex gap={cq({ base: 'md', lg: 'xl', xl: 'xxl' })} align="flex-start" wrap="nowrap">
          <Sidebar links={links} activeUrl={activeUrl} />
          <Box style={{ flex: 1, minWidth: 0 }}>{children}</Box>
        </Flex>
      )}
      <Drawer opened={opened && isCollapsed} onClose={close} position="left" size="sm" lockScroll>
        <Sidebar links={links} activeUrl={activeUrl} fullWidth onItemSelect={close} />
      </Drawer>
    </SidebarMenuContext.Provider>
  );
}

function renderInlineFallback(
  inlineFallback: ReactNode,
  hasCustomTrigger: boolean,
  firstRender: boolean,
) {
  if (hasCustomTrigger) return null;
  if (firstRender) return null;
  return inlineFallback;
}
